<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import api from '@/api';
import { MAX_IP_ADDRESS_RESTRICTION_DESCRIPTION } from '@/constants';
import { DgrDialog } from '@stockmarkteam/donguri-ui';
import LoadableButton from '@/components/common/atoms/loadable-button.vue';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import { IpAddressRestriction } from '@/types';
import { validateIpAddress } from '@/utils/ipAddress';

// このコンポーネントはDgrDialogをラップしているだけなので、openの値をこのコンポーネント内で変更しない。
const open = defineModel<boolean>('open', { default: false });

const emit = defineEmits<{
  (e: 'onClose'): void;
  (e: 'onSubmit'): void;
}>();

const { createSnackbar } = useSnackbar();

const isEditing = ref(false);

const props = defineProps<{
  ipAddressRestriction?: IpAddressRestriction;
}>();

const ipAddress = ref('');
const description = ref('');
const id = ref<number | undefined>(undefined);

watch(
  () => open.value,
  () => {
    if (open.value) {
      const restriction = props.ipAddressRestriction;
      ipAddress.value = restriction?.ip_address || '';
      description.value = restriction?.description || '';
      id.value = restriction?.id;
    } else {
      emit('onClose');
    }
  },
);

const isInvalidIpAddress = computed(() => !validateIpAddress(ipAddress.value));

const isDescriptionTooLong = computed(
  () => description.value.length > MAX_IP_ADDRESS_RESTRICTION_DESCRIPTION,
);

const isEmptyIpAddress = computed(() => ipAddress.value === '');

const enableToEdit = computed(
  () =>
    !isInvalidIpAddress.value &&
    !isEmptyIpAddress.value &&
    !isDescriptionTooLong.value,
);

const editIpAddressRestriction = async () => {
  try {
    if (!id.value) {
      throw new Error('idがありません');
    }
    isEditing.value = true;
    const editedIpAddressRestriction = {
      id: id.value,
      ip_address: ipAddress.value,
      description: description.value,
    };
    await api.putIpAddressRestriction(editedIpAddressRestriction);
    createSnackbar({
      message: 'IPアドレスの設定を変更しました',
      type: 'success',
    });
    emit('onSubmit');
  } catch {
    createSnackbar({
      message: 'IPアドレスの設定を変更できませんでした',
      type: 'error',
    });
  }
  isEditing.value = false;
  open.value = false;
};
</script>

<template>
  <DgrDialog v-model:open="open" title="IPアドレスの編集" with-footer-divider>
    <template #description>
      <div class="modal-form">
        <div class="c-formBlock">
          <div class="c-formBlock__label">IPアドレス</div>
          <div
            class="c-formBlock__text modal-error-message"
            v-if="isInvalidIpAddress && !isEmptyIpAddress"
          >
            <span class="c-text-regular--s"
              >不正なIPアドレスが入力されています。</span
            >
          </div>
          <div
            class="c-formBlock__text modal-error-message"
            v-if="isEmptyIpAddress"
          >
            <span class="c-text-regular--s">IPアドレスが空欄です。</span>
          </div>
          <input
            class="c-text-regular--m c-textInput"
            v-model="ipAddress"
            :class="{
              'c-formInput--error': isEmptyIpAddress || isInvalidIpAddress,
            }"
          />
          <div class="spacing-08"></div>
        </div>
        <div class="spacing-08"></div>
        <div class="c-formBlock">
          <div class="c-formBlock__label">備考</div>
          <input
            class="c-text-regular--m c-textInput"
            v-model="description"
            :class="{
              'c-formInput--error': isDescriptionTooLong,
            }"
          />
          <div
            :class="{
              'modal-error-message': isDescriptionTooLong,
            }"
            class="modal-description-word-count"
          >
            <span class="c-text-regular--xs" v-if="isDescriptionTooLong">
              {{
                MAX_IP_ADDRESS_RESTRICTION_DESCRIPTION
              }}文字以内で入力してください。
            </span>
            <span class="c-text-regular--xs">
              {{
                description.length +
                '/' +
                MAX_IP_ADDRESS_RESTRICTION_DESCRIPTION
              }}
            </span>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="modal-footer">
        <div class="modal-ip-address-annotation c-text-regular--s">
          <div>
            IPv4の通常の表記及びCIDR表記に対応しています(一括で最大10件登録可能)
          </div>
          <div class="spacing-08"></div>
          <div>通常の表記（例 203.0.113.0）CIDR表記（例 203.0.113.0/24）</div>
        </div>
        <div class="modal-footer-buttons">
          <button
            class="modal-cancel-button c-btn c-btn--auto c-btnOutline"
            @click="open = false"
          >
            キャンセル
          </button>

          <LoadableButton
            class="modal-create-button c-btn c-btn--auto c-btn--AnewsPrimary"
            :class="{ disabled: !enableToEdit }"
            :disabled="!enableToEdit"
            @click="editIpAddressRestriction"
            :is-loading="isEditing"
          >
            保存
          </LoadableButton>
        </div>
      </div>
    </template>
  </DgrDialog>
</template>

<style lang="scss" scoped>
.modal-form {
  width: 676px;

  .modal-description-word-count {
    justify-content: flex-end;
    color: $color-gray600;
  }

  .modal-error-message {
    color: $color-orange1000;
  }
}

.modal-footer {
  width: 100%;
  display: flex;
  justify-content: space-between;

  .modal-ip-address-annotation {
    display: flex;
    flex-flow: column;
    justify-content: flex-start;
    color: $color-gray600;
  }

  .modal-footer-buttons {
    display: flex;
    flex-direction: row;
    gap: 16px;
  }
}
</style>
